// SPDX-FileCopyrightText: Copyright 2025 OP Financial Group (https://op.fi). All Rights Reserved.
//
// SPDX-License-Identifier: LicenseRef-OpAllRightsReserved

import React, { useEffect, useState, useContext, useRef } from 'react';
import message from '../messages';
import { AnimCheck, Alert, Col, Loading, Row, Button } from '@op/opds.opux';
import { agreementTypes, mapPdfErrorMessageContent, getPdf } from '../components/PdfHandler';
import UseWindowWidth from '../hooks/useWindowWidth';
import { pageRoutes } from '../Constants';
import LinkToMainPage from '../components/LinkToMainPage';
import './thankyou.scss';
import { ApiResponse, ContextWrapper } from '../context/ContextWrapper';
import client from '../api/RestApi';

interface PdfState {
    isLoading: boolean;
    error: boolean;
}

const ThankYou: React.FC = () => {
    const viewThreshold: number = 375;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [pdfState, setPdfState] = useState<PdfState>({
        isLoading: false,
        error: false,
    });
    const width: number = UseWindowWidth();
    const { isAdminFlow } = useContext(ContextWrapper);
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            // Use a flag to prevent double execution in React StrictMode
            if (hasInitialized.current) return;
            hasInitialized.current = true;
        }
        const currentUrl = new URLSearchParams(window.location.search);
        client
            .get('/email?code=' + currentUrl.get('code') + '&&state=' + currentUrl.get('state'))
            .then((response: ApiResponse) => {
                if (response.status === 'FAILURE') {
                    window.location.hash = pageRoutes.error;
                } else {
                    setIsLoading(false);
                    try {
                        let language = document.documentElement.getAttribute('lang');
                        if (window._satellite && !isAdminFlow) {
                            window._satellite.track('application_phase', {
                                phase_category: 'completed',
                                name: 'vv ya verkkopalvelusopimus',
                                group: 'self-service',
                                business_group: 'insurance',
                                channel: 'ya',
                                phase: 'lomake taytetty',
                                language: { language }, // valid: fi_fi en_us sv_se
                            });
                        }
                    } catch (_e) {
                        void _e; // NOSONAR
                    }
                }
            })
            .catch(() => {
                window.location.hash = pageRoutes.error;
            });
    }, [isAdminFlow]);

    if (isLoading) {
        return (
            <div className="opux-spacing-top-margin-double opux-align-center">
                <Loading />
            </div>
        );
    }

    return (
        <div id="thankYouPage" data-testid="thankYouPage">
            <div
                className={
                    width > viewThreshold
                        ? 'opux-spacing-top-margin-double opux-align-center heading-margin-top-50'
                        : 'opux-spacing-top-margin-double opux-align-center'
                }
                data-testid="thankYouHeader"
            >
                <AnimCheck size={100} />
                <div className="opux-spacing-top-margin opux-align-center">
                    <h1 className="h1-fixed-size" data-testid="thankYouHeading">
                        {message('thankYouHeading')}
                    </h1>
                </div>
            </div>
            <div className="opux-spacing-top-margin-double opux-align-center" data-testid="thankYouMessage">
                <div>{isAdminFlow ? message('adminFlowSuccessMessage') : message('successMessage')}</div>
            </div>
            <div className="steps-margin" data-testid="thankYouSteps">
                <Row middle="xs" className="step-bullet row-formatting">
                    <Col className="number-circle circle-size">1</Col>
                    <Col xs className="step-col-formatting">
                        {isAdminFlow ? message('adminFlowSuccessStep1') : message('successStep1')}
                    </Col>
                </Row>
                {pdfState.isLoading && (
                    <div className="opux-spacing-top-margin-double opux-align-center" data-testid="pdfLoading">
                        <Loading />
                    </div>
                )}
                {pdfState.error && (
                    <div className="opux-spacing-top-margin-double" data-testid="pdfError">
                        <Alert type="critical">
                            {message('noPdfAlertElectronicSigningText')}
                            {mapPdfErrorMessageContent(pdfState)}
                        </Alert>
                    </div>
                )}
                <div className="opux-align-center opux-spacing-top-margin">
                    <Button
                        primary
                        onClick={() => {
                            setPdfState({ error: false, isLoading: true });
                            getPdf(agreementTypes.electronicSigning, setPdfState);
                        }}
                        data-testid="downloadPdfButton"
                    >
                        {message('downloadPdf')}
                    </Button>
                </div>
                <Row middle="xs" className="step-bullet row-formatting">
                    <Col className="number-circle circle-size">2</Col>
                    <Col xs className="step-col-formatting">
                        {isAdminFlow ? message('adminFlowSuccessStep2') : message('successStep2')}
                    </Col>
                </Row>
                <Row middle="xs" className="step-bullet row-formatting">
                    <Col className="number-circle circle-size">3</Col>
                    <Col xs className="step-col-formatting">
                        {isAdminFlow ? message('adminFlowSuccessStep3') : message('successStep3')}
                    </Col>
                </Row>
            </div>
            <div className="opux-spacing-top-margin-double opux-align-center" data-testid="supportContact">
                <div>{message('supportContact')}</div>
            </div>
            <LinkToMainPage />
        </div>
    );
};

export default ThankYou;
